import { useState } from 'react';
import { membershipService } from 'services/api';
import { RENEW_OPTIONS, PLAN_OPTIONS, asambleasIncluidas, txtAsambleas } from './Memberships';
import styles from './MembershipPlanModal.module.css';

const MAX_DIAS = 3650; // 10 años — mismo tope que valida el backend (membershipsController.js)

export default function MembershipPlanModal({ property, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const isPlanAsamblea  = property.subscription_type === 'asamblea';
  // Estándar/Premium piden período (mismo selector que "Renovar") porque las
  // asambleas incluidas dependen de cuánto se compra — asamblea/logística
  // tienen duración fija, se activan directo sin pedir período.
  const [periodFor,   setPeriodFor]   = useState(null); // 'standard' | 'premium' | null
  const [customDays,  setCustomDays]  = useState('');
  // F-05: un período que no incluye asambleas pide confirmación antes de activar.
  const [confirmarSinCupo, setConfirmarSinCupo] = useState(null); // { plan, days } | null

  const activate = async (plan, days) => {
    if ((plan === 'standard' || plan === 'premium') &&
        (!Number.isInteger(days) || days < 1 || days > MAX_DIAS)) {
      return setError(`Los días deben ser un número entero entre 1 y ${MAX_DIAS}`);
    }
    setLoading(true); setError('');
    try {
      if (plan === 'asamblea')       await membershipService.activarAsamblea(property.id);
      else if (plan === 'logistica') await membershipService.activarLogistica(property.id);
      else if (plan === 'premium')   await membershipService.activarPremium(property.id, days);
      else                           await membershipService.activarStandard(property.id, days);
      onSaved(
        plan === 'asamblea'  ? 'Plan Asamblea activado — válido por 2 semanas' :
        plan === 'logistica' ? 'Plan Asamblea + Logística activado' :
        plan === 'premium'   ? `Plan Premium activado por ${days} días — incluye ${txtAsambleas(asambleasIncluidas('premium', days))}` :
                               `Plan Estándar activado por ${days} días — incluye ${txtAsambleas(asambleasIncluidas('standard', days))}`
      );
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cambiar el plan');
      setLoading(false);
    }
  };

  // F-05: antes los botones de período activaban de una: un Premium de 1 mes
  // quedaba "Activa" con 0 asambleas y nadie se enteraba hasta crear una.
  const elegirPeriodo = (plan, days) => {
    if (asambleasIncluidas(plan, days) === 0) return setConfirmarSinCupo({ plan, days });
    activate(plan, days);
  };

  const handlePlanClick = (planKey) => {
    if (loading) return;
    if (planKey === 'standard' || planKey === 'premium') { setPeriodFor(planKey); return; }
    activate(planKey);
  };

  // Calcular expiración estimada (14 días desde hoy)
  const expires = new Date();
  expires.setDate(expires.getDate() + 14);
  const expiresStr = expires.toLocaleDateString('es-CO', { day:'2-digit', month:'long', year:'numeric' });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${styles.caja}`} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3><i className={`fas fa-layer-group ${styles.iconoTitulo}`} />Cambiar plan</h3>
            <div className={styles.subtitulo}>
              {property.name}
            </div>
          </div>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        <div className={`modal-body ${styles.cuerpo}`}>
          {error && <div className="alert alert-danger">{error}</div>}

          {PLAN_OPTIONS.map(plan => {
            const isActive = property.subscription_type === plan.key;
            return (
              <div key={plan.key}
                className={isActive ? `${styles.plan} ${styles.planActivo}` : `${styles.plan} ${styles.planInactivo}`}
                style={{ '--tono': plan.color, '--fondo-suave': plan.color + '0d', '--fondo-icono': plan.color + '18', '--fondo-chip': plan.color + '22' }}
                onClick={() => !isActive && handlePlanClick(plan.key)}>
                <div className={styles.planIcono}>
                  <i className={`fas ${plan.icon} ${styles.planGlifo}`} />
                </div>
                <div className={styles.planTexto}>
                  <div className={styles.planNombre}>
                    {plan.label}
                    {isActive && <span className={styles.planActual}>Plan actual</span>}
                  </div>
                  <div className={styles.planDesc}>{plan.desc}</div>
                  {plan.key === 'asamblea' && !isActive && (
                    <div className={styles.planFecha}>
                      <i className={`fas fa-calendar ${styles.fechaIcono}`} />Expira: {expiresStr}
                    </div>
                  )}
                  {plan.key === 'asamblea' && isActive && property.subscription_expires_at && (
                    <div className={styles.planFecha}>
                      <i className={`fas fa-calendar ${styles.fechaIcono}`} />
                      Vence: {new Date(property.subscription_expires_at).toLocaleDateString('es-CO', { day:'2-digit', month:'long', year:'numeric' })}
                    </div>
                  )}
                </div>
                {!isActive && (
                  <div className={styles.activar}>
                    {loading ? <i className="fas fa-spinner fa-spin" /> : 'Activar →'}
                  </div>
                )}
              </div>
            );
          })}

          {/* Selector de período — solo para Estándar/Premium, las asambleas
              incluidas dependen de cuánto se compra (MEMB-03). */}
          {periodFor && (
            <div className={styles.periodo}>
              <div className={styles.periodoTitulo}>
                Período de {periodFor === 'premium' ? 'Premium' : 'Estándar'}
              </div>
              <div className={styles.periodoOpciones}>
                {RENEW_OPTIONS.map(opt => (
                  <button key={opt.days} type="button" disabled={loading}
                    onClick={() => elegirPeriodo(periodFor, opt.days)}
                    className={styles.opcionPeriodo}>
                    {opt.label}
                    <span className={asambleasIncluidas(periodFor, opt.days) === 0 ? `${styles.opcionCupo} ${styles.sinCupo}` : styles.opcionCupo}>
                      {txtAsambleas(asambleasIncluidas(periodFor, opt.days))}
                    </span>
                  </button>
                ))}
              </div>
              <div className={styles.diasFila}>
                <input type="number" min={1} max={MAX_DIAS} placeholder="Días personalizados" value={customDays}
                  onChange={e => setCustomDays(e.target.value)}
                  className={styles.diasInput} />
                <button type="button" className="btn btn-sm btn-primary" disabled={loading || !customDays}
                  onClick={() => elegirPeriodo(periodFor, parseInt(customDays))}>
                  Activar
                </button>
              </div>
              {asambleasIncluidas(periodFor, customDays) !== null && (
                <div className={asambleasIncluidas(periodFor, customDays) === 0 ? `${styles.diasTexto} ${styles.sinCupo}` : styles.diasTexto}>
                  {customDays} días incluyen {txtAsambleas(asambleasIncluidas(periodFor, customDays))}
                </div>
              )}
              {confirmarSinCupo && (
                <div className={`alert alert-warning ${styles.avisoSinCupo}`}>
                  <i className={`fas fa-triangle-exclamation ${styles.avisoIcono}`} />
                  <strong>Este período no incluye asambleas.</strong> El conjunto tendrá la administración,
                  pero no podrá crear asambleas salvo que le agregues asambleas extra. Con{' '}
                  {confirmarSinCupo.plan === 'premium' ? 'Premium, desde 6 meses' : 'Estándar, desde 1 año'} ya incluye asambleas.
                  <div className={styles.avisoBotones}>
                    <button type="button" className="btn btn-sm btn-secondary" disabled={loading}
                      onClick={() => setConfirmarSinCupo(null)}>Elegir otro período</button>
                    <button type="button" className="btn btn-sm btn-primary" disabled={loading}
                      onClick={() => { const c = confirmarSinCupo; setConfirmarSinCupo(null); activate(c.plan, c.days); }}>
                      Activar de todos modos
                    </button>
                  </div>
                </div>
              )}
              <button type="button" onClick={() => { setPeriodFor(null); setConfirmarSinCupo(null); }}
                className={styles.cancelar}>
                Cancelar
              </button>
            </div>
          )}

          {/* Info planes */}
          {!isPlanAsamblea && property.subscription_type !== 'logistica' && (
            <div className={`${styles.info} ${styles.infoAsamblea}`}>
              <i className={`fas fa-info-circle ${styles.infoIcono}`} />
              <strong>Plan Asamblea:</strong> El administrador obtendrá privilegios de Admin Asamblea
              durante <strong>2 semanas</strong>: asistentes, preguntas, resultados y acta PDF.
            </div>
          )}
          {property.subscription_type !== 'logistica' && property.subscription_type !== 'asamblea' && (
            <div className={`${styles.info} ${styles.infoLogistica}`}>
              <i className={`fas fa-info-circle ${styles.infoIcono}`} />
              <strong>Plan Asamblea + Logística:</strong> El admin puede gestionar torres, unidades
              y propietarios, pero <strong>no tiene acceso al módulo de asambleas</strong>.
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
